import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SubdomainInfo {
  isSubdomain: boolean;
  subdomain: string | null;
  coachId: string | null;
  isLoading: boolean;
  error: string | null;
}

// Platform domains that should NOT be treated as coach subdomains
const PLATFORM_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'lovable.app',
  'lovableproject.com',
  'coachpod.ai', // Main platform domain
  'gocoachify.com', // Legacy platform domain
  'gocoachify.com', // Production platform domain
  'www',
  'app',
  'admin',
  'api',
];

export const useSubdomain = (): SubdomainInfo => {
  const [info, setInfo] = useState<SubdomainInfo>({
    isSubdomain: false,
    subdomain: null,
    coachId: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const detectSubdomain = async () => {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // Check if it's a known platform domain (not a coach subdomain)
      const isKnownPlatformDomain = PLATFORM_DOMAINS.some(domain =>
        hostname === domain || hostname.endsWith(`.${domain}`)
      );

      // For gocoachify.com/ai (production) - always treat as platform
      if (hostname === 'gocoachify.com' || hostname === 'www.gocoachify.com' ||
          hostname === 'gocoachify.com' || hostname === 'www.gocoachify.com') {
        setInfo({ isSubdomain: false, subdomain: null, coachId: null, isLoading: false, error: null });
        return;
      }

      // Check for lovable.app preview domains (e.g., subdomain.id--hash.lovable.app)
      if (hostname.includes('lovable.app') || hostname.includes('lovableproject.com')) {
        // Extract subdomain from lovable preview URL pattern
        // Pattern: subdomain--projectid.lovable.app or just projectid.lovable.app
        const match = hostname.match(/^([a-z0-9-]+)--[a-z0-9-]+\.lovable\.app$/i);
        if (match) {
          const subdomain = match[1];
          await lookupCoach(subdomain);
          return;
        }
        // No subdomain in lovable preview
        setInfo({ isSubdomain: false, subdomain: null, coachId: null, isLoading: false, error: null });
        return;
      }

      // For custom domains or localhost
      // Localhost with subdomain simulation via query param
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        const urlParams = new URLSearchParams(window.location.search);
        const simulatedSubdomain = urlParams.get('subdomain');
        if (simulatedSubdomain) {
          await lookupCoach(simulatedSubdomain);
          return;
        }
        setInfo({ isSubdomain: false, subdomain: null, coachId: null, isLoading: false, error: null });
        return;
      }

      // For production: check if it's a subdomain of the platform
      // e.g., coach-name.coachpod.ai or coach-name.gocoachify.com
      if (parts.length >= 3) {
        const potentialSubdomain = parts[0];
        if (!PLATFORM_DOMAINS.includes(potentialSubdomain.toLowerCase())) {
          await lookupCoach(potentialSubdomain);
          return;
        }
      }

      // If it's a known platform domain with 2 parts (e.g., gocoachify.com), don't check custom domain
      if (isKnownPlatformDomain) {
        setInfo({ isSubdomain: false, subdomain: null, coachId: null, isLoading: false, error: null });
        return;
      }

      // Check if it's a custom domain pointing to a coach
      await lookupCoachByCustomDomain(hostname);
    };

    const lookupCoach = async (subdomain: string) => {
      try {
        const { data, error } = await supabase
          .from('coaches')
          .select('id, is_active')
          .eq('website_subdomain', subdomain)
          .maybeSingle();

        if (error) throw error;

        if (data && data.is_active) {
          setInfo({
            isSubdomain: true,
            subdomain,
            coachId: data.id,
            isLoading: false,
            error: null,
          });
        } else if (data && !data.is_active) {
          setInfo({
            isSubdomain: true,
            subdomain,
            coachId: null,
            isLoading: false,
            error: 'This coach website is currently inactive.',
          });
        } else {
          setInfo({
            isSubdomain: false,
            subdomain: null,
            coachId: null,
            isLoading: false,
            error: null,
          });
        }
      } catch (err: any) {
        setInfo({
          isSubdomain: false,
          subdomain: null,
          coachId: null,
          isLoading: false,
          error: err.message,
        });
      }
    };

    const lookupCoachByCustomDomain = async (domain: string) => {
      try {
        const { data, error } = await supabase
          .from('coaches')
          .select('id, is_active, website_subdomain')
          .eq('custom_domain', domain)
          .maybeSingle();

        if (error) throw error;

        if (data && data.is_active) {
          setInfo({
            isSubdomain: true,
            subdomain: data.website_subdomain,
            coachId: data.id,
            isLoading: false,
            error: null,
          });
        } else if (data && !data.is_active) {
          setInfo({
            isSubdomain: true,
            subdomain: data.website_subdomain,
            coachId: null,
            isLoading: false,
            error: 'This coach website is currently inactive.',
          });
        } else {
          setInfo({
            isSubdomain: false,
            subdomain: null,
            coachId: null,
            isLoading: false,
            error: null,
          });
        }
      } catch (err: any) {
        setInfo({
          isSubdomain: false,
          subdomain: null,
          coachId: null,
          isLoading: false,
          error: err.message,
        });
      }
    };

    detectSubdomain();
  }, []);

  return info;
};

// Utility function to generate the path-based coach website URL
export const getCoachWebsiteUrl = (subdomain: string): string => {
  return `${window.location.origin}/${subdomain}`;
};

// Get the platform base domain for display
export const getPlatformBaseDomain = (): string => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'coachpod.ai';
  }

  if (hostname.includes('lovable.app')) {
    const match = hostname.match(/([a-z0-9]+\.lovable\.app)$/i);
    return match ? match[1] : 'lovable.app';
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return parts.slice(-2).join('.');
  }

  return hostname;
};
